import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useState, useRef, useEffect } from 'react';
import SideMenu from './SideMenu';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLangOpen, setIsLangOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const langRef = useRef<HTMLDivElement>(null);
  const isEnglish = location.pathname === '/en' || location.pathname === '/en/' || location.pathname.startsWith('/en/');

  const withEn = (path: string): string => {
    if (!isEnglish) return path;
    if (!path.startsWith('/')) return path;
    if (path.startsWith('/en')) return path;
    if (path.startsWith('/blog')) return path;
    if (path.startsWith('http')) return path;
    if (path.startsWith('#')) return path;
    if (path === '/') return '/en';
    return '/en' + path;
  };

  const toEnglish = (pathname: string): string => {
    if (pathname.startsWith('/blog')) return pathname;
    if (pathname === '/en' || pathname.startsWith('/en/')) return pathname;
    if (pathname === '/') return '/en';
    return '/en' + pathname;
  };

  const toJapanese = (pathname: string): string => {
    if (pathname.startsWith('/blog')) return pathname;
    if (pathname === '/en') return '/';
    if (pathname.startsWith('/en/')) return pathname.substring(3);
    return pathname;
  };

  const switchLanguage = (lang: 'ja' | 'en') => {
    const targetPath = lang === 'en' ? toEnglish(location.pathname) : toJapanese(location.pathname);
    const fullPath = targetPath + location.search + location.hash;
    navigate(fullPath);
    setIsLangOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(event.target as Node)) {
        setIsLangOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsLangOpen(false);
      }
    };

    if (isLangOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isLangOpen]);

  const navItems = [
    { label: isEnglish ? 'Home' : 'ホーム', path: '/' },
    { label: isEnglish ? 'Filament Data' : 'フィラメントデータ', path: '/filament' },
    { label: isEnglish ? 'Troubleshooting' : 'トラブル対策', path: '/troubleshooting' },
    { label: isEnglish ? 'Blog' : 'ブログ', path: '/blog/', isExternal: true },
    { label: isEnglish ? 'For Beginners' : '初心者向け', path: '/beginner' },
    { label: isEnglish ? 'Useful Knowledge' : '役立ち関連知識', path: '/knowledge' }
  ];

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  // アクティブ判定：完全一致またはサブパス判定（外部リンクも含む）
  const isActiveNav = (itemPath: string) => {
    if (itemPath === '/') {
      return location.pathname === '/' || location.pathname === '/en' || location.pathname === '/en/';
    }
    // /blog/ の場合も判定に含める
    if (itemPath === '/blog/') {
      return location.pathname === '/blog/' || location.pathname.startsWith('/blog/');
    }
    const checkPath = itemPath.replace(/^\/en/, '');
    const currentPath = location.pathname.replace(/^\/en/, '');
    return currentPath === checkPath || currentPath.startsWith(checkPath + '/');
  };

  return (
    <>
      <header className="bg-metal border-b border-metal sticky top-0 z-40 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Hamburger Menu Button */}
            <button
              onClick={toggleMenu}
              className="w-10 h-10 flex items-center justify-center text-metal-light hover:text-white transition-colors"
              aria-label={isEnglish ? 'Open menu' : 'メニューを開く'}
            >
              <i className="ri-menu-line text-2xl"></i>
            </button>

            {/* Logo and Site Title */}
            <Link to={withEn('/')} className="flex items-center space-x-3 group">
              <img 
                src="/assets/nag-logo-512.png" 
                alt={isEnglish ? 'Sekisonagnote Logo' : 'せきそう NAG ノート ロゴ'}
                className="w-12 h-12 object-contain transition-transform group-hover:scale-110"
              />
              <div className="flex flex-col">
                <span className="text-white text-xl font-bold font-['Exo'] tracking-wide group-hover:text-accent transition-colors">
                  せきそうNAGノート
                </span>
                <span className="text-metal-light text-xs font-['Roboto_Mono'] tracking-wider">
                  FDM 3D Printer Lab
                </span>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-1">
              {navItems.map((item) => {
                const isActive = isActiveNav(item.path);
                
                if (item.isExternal) {
                  return (
                    <a
                      key={item.path}
                      href={item.path}
                      className={`px-4 py-2 text-sm font-medium transition-all whitespace-nowrap ${
                        isActive
                          ? 'text-accent border-b-2 border-accent'
                          : 'text-metal-light hover:text-white hover:bg-metal-dark'
                      }`}
                    >
                      {item.label}
                    </a>
                  );
                }
                
                return (
                  <Link
                    key={item.path}
                    to={withEn(item.path)}
                    className={`px-4 py-2 text-sm font-medium transition-all whitespace-nowrap ${
                      isActive
                        ? 'text-accent border-b-2 border-accent'
                        : 'text-metal-light hover:text-white hover:bg-metal-dark'
                    }`}
                  >
                    {item.label}
                  </Link>
                );
              })}
              
              {/* Language Switcher */}
              <div className="relative" ref={langRef}>
                <button
                  onClick={() => setIsLangOpen(!isLangOpen)}
                  className="w-10 h-10 flex items-center justify-center text-metal-light hover:text-white transition-colors"
                  aria-label="Language"
                >
                  <i className="ri-global-line text-xl"></i>
                </button>
                
                {isLangOpen && (
                  <div className="absolute right-0 mt-2 w-32 bg-metal border border-metal-dark rounded shadow-lg">
                    <button
                      onClick={() => switchLanguage('ja')}
                      className={`w-full px-4 py-2 text-left text-sm transition-colors whitespace-nowrap ${
                        !isEnglish
                          ? 'text-accent font-medium'
                          : 'text-metal-light hover:text-white hover:bg-metal-dark'
                      }`}
                    >
                      日本語
                    </button>
                    <button
                      onClick={() => switchLanguage('en')}
                      className={`w-full px-4 py-2 text-left text-sm transition-colors whitespace-nowrap ${
                        isEnglish
                          ? 'text-accent font-medium'
                          : 'text-metal-light hover:text-white hover:bg-metal-dark'
                      }`}
                    >
                      English
                    </button>
                  </div>
                )}
              </div>
            </nav>

            {/* Mobile Menu Spacer */}
            <div className="md:hidden w-10"></div>
          </div>
        </div>
      </header>

      {/* Side Menu Component */}
      <SideMenu isOpen={isMenuOpen} onClose={() => setIsMenuOpen(false)} isEnglish={isEnglish} />
    </>
  );
};

export default Header;
